import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Data Extractor — Tier 1.5.
 *
 * Slice 1 schemas: per-tenant `extractor_runs` + shared `extractor_templates`.
 *
 * Firestore paths:
 *   - tenants/{tenantId}/extractor_runs/{runId}     — team-shared, gated
 *     by `confidential_user` / `kb_admin`. The privacy boundary is the
 *     RBAC check (confidential clearance), not per-user isolation — CS
 *     teams have shared queues and operator handoffs.
 *   - extractor_templates/{templateId}              — shared across all
 *     tenants; CRUD restricted to `kb_admin`. See CLAUDE.md documented
 *     exceptions list.
 *
 * v1 scope (locked 2026-05-20):
 *   - 5 vendors: Telkom, Vodacom, BTC, Paratus, DimensionData.
 *   - Output: partial 36-col CSV in the `GncWanBlFl` shape — vendor-
 *     derivable columns populated, master-data columns (StoreCode, Trend,
 *     Site, Installation/Cancellation) left empty. v1.1 adds the master-
 *     data join.
 *   - Per-invoice cost cap default $35 USD (Vodacom 976pp = $29 reality).
 *   - Monthly budget cap $500 USD.
 *
 * State machine (per plan §3 Phase 3 finding #4):
 *
 *   pending → extracting → validating → ready
 *   pending → extracting → partial   (one+ batches failed terminally)
 *   partial → extracting             (retry failed batches)         [Slice 2+]
 *   partial → validating             (operator accepted partial)    [Slice 2+]
 *   partial → failed                 (retry exhausted; abort)
 *   extracting → failed              (preflight error; cost cap)
 *
 * Refinement: refining a run spawns a child run with parentRunId set and
 * the parent's templateVersion snapshot (NOT the current active version)
 * so the refinement applies the same rules the parent saw. Depth-capped
 * by backend MAX_REFINEMENT_DEPTH (5) to prevent cycles.
 */
export const ExtractorRunStatus = z.enum([
    'pending',
    'extracting',
    'validating',
    'ready',
    'partial',
    'failed',
]);
/**
 * Vendor is a free-form string from 2026-06 onward — admins create
 * templates for new vendors via the UI without a schema bump. Earlier
 * closed enum (Telkom/Vodacom/BTC/Paratus/DimensionData/unknown) is
 * preserved as `LEGACY_EXTRACTOR_VENDORS` for the seed templates and
 * the back-compat hardcoded-mapper fallback in extractor-job.py.
 * `unknown` remains the sentinel used before the classifier runs.
 */
export const ExtractorVendor = z.string().min(1).max(80);
export const LEGACY_EXTRACTOR_VENDORS = [
    'Telkom',
    'Vodacom',
    'BTC',
    'Paratus',
    'DimensionData',
    'unknown',
];
/**
 * Output format. v1 targets `GncWanBlFl` (Generic WAN Billing File) — the
 * 36-col OneView import shape derived from
 * `Geminga_Admin/Adhoc Scripts/ImportDefinition/GncWanBlFl.sql`. Sibling
 * formats (WanVPNBlFl, WanMetBlFl, etc.) are deferred.
 */
/**
 * Back-compat alias for `outputShape`. Was a closed enum in v1; now a free
 * slug so the template schema and the run-level format stay in lockstep.
 */
export const ExtractorFormat = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/, 'format must be a slug');
export const ExtractorReconciliation = z.enum(['match', 'mismatch', 'unknown']);
/**
 * Reconciliation breakdown persisted on the run doc so the frontend can
 * render the panel without re-reading the GCS sidecar. The 11 cost-bucket
 * names mirror `agents/.../extractor/cost_buckets.py::COST_BUCKET_COLUMNS`.
 * `perBucket` is a free-form map (string keys) rather than a closed object
 * so the schema doesn't need a coordinated bump every time the bucket list
 * shifts in v1.1.
 */
export const ExtractorReconciliationSummary = z.object({
    docaiGrandTotal: z.number().nullable(),
    docaiGrandTotalSource: z.string().max(200).nullable(),
    bucketSum: z.number(),
    delta: z.number(),
    deltaPercent: z.number().nullable(),
    perBucket: z.record(z.string(), z.number()),
    rowCount: z.number().int().nonnegative(),
    computedAt: IsoTimestamp,
});
/**
 * Per-batch tracker — DocAI Invoice Parser is chunked at ~15 pages per call
 * because a multi-page request mis-picks line-item values as the grand
 * total (see spike memo §"Run B"). Each batch is retryable independently
 * so a transient 503 on pages 31-40 doesn't blow away successful 1-30/
 * 41-N batches.
 */
export const ExtractorBatch = z.object({
    id: z.string().min(1).max(64),
    pageRangeStart: z.number().int().nonnegative(),
    pageRangeEnd: z.number().int().nonnegative(),
    attempts: z.number().int().nonnegative().default(0),
    status: z.enum(['pending', 'running', 'ready', 'failed']),
    outputGcsPath: z.string().max(500).optional(),
    costUsd: z.number().nonnegative().default(0),
    errorMessage: z.string().max(2000).optional(),
    startedAt: IsoTimestamp.optional(),
    finishedAt: IsoTimestamp.optional(),
});
/**
 * Supported source MIME types. PDFs for 4 vendors; xlsx for Dimension Data.
 */
export const EXTRACTOR_ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
export const EXTRACTOR_MIME_TO_EXT = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};
export const ExtractorTemplateStatus = z.enum(['draft', 'active']);
/**
 * Output shape is now a free slug — admins author templates with their own
 * column sets via the UI. `GncWanBlFl` remains the back-compat path: when
 * an active template carries that slug AND no explicit `columns[]`, the
 * Python mapper falls back to the hardcoded 36-col GncWanBlFl pipeline.
 * Any other slug requires `columns[]` to be defined.
 */
export const ExtractorOutputShape = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/, 'output shape must be a slug');
export const LEGACY_OUTPUT_SHAPE_GNCWANBLFL = 'GncWanBlFl';
/**
 * Per-template column definition. Drives the schema-driven mapper +
 * reconciler in extractor-job.py — replaces the hardcoded GncWanBlFl
 * 36-col pipeline for non-legacy templates.
 *
 * `name` is both the CSV header and the JSON key the Vision rows-pass
 * is asked to emit. The rowsPrompt should list every column by name so
 * the model can be evaluated against the contract.
 */
export const ExtractorColumn = z.object({
    name: z.string().min(1).max(120),
    type: z.enum(['string', 'number', 'date', 'enum']),
    required: z.boolean().default(false),
    /**
     * Marks the dedup / identity column (e.g. service-id, request number).
     * At most one column per template should set this — the mapper uses it
     * for chunk-boundary dedup. Optional; when absent the mapper falls back
     * to row-content hashing.
     */
    isKey: z.boolean().optional(),
    /**
     * When true, the column's numeric values are summed for reconciliation
     * against the document grand total. Replaces the hardcoded 11 cost-
     * bucket columns in the legacy GncWanBlFl mapper. Only valid for
     * `type: 'number'`.
     */
    isReconcileNumeric: z.boolean().optional(),
    /** Free-text guidance shown in the admin UI and inlined into the prompt header. */
    notes: z.string().max(500).optional(),
    /** For `type: 'enum'` — closed value set. Ignored otherwise. */
    enumValues: z.array(z.string().min(1).max(120)).max(50).optional(),
    /**
     * For structured (xlsx) sources: the source column header this output
     * column is read from, when it differs from `name`. Lets a template map
     * a spreadsheet's own headers (e.g. "Total") onto a OneView output column
     * (e.g. "ChargeControl1"). Absent → the reader falls back to matching by
     * `name`. Ignored by the Vision (PDF) path, which prompts on `name`.
     */
    sourceHeader: z.string().min(1).max(120).optional(),
    /**
     * For `type: 'number'` structured sources: divide the source cell by this
     * factor (e.g. 100 for a cents→rands file). Absent → no scaling. The
     * Vodacom xlsx is already in rands so it omits this; the fixed-width
     * CrptFl source would set 100.
     */
    sourceScale: z.number().positive().optional(),
});
/**
 * How the source rows are reshaped to reach a destination format.
 *   - `flat`    — one output row per input row (column rename/compute only).
 *   - `unpivot` — wide per-entity rows are melted into itemised lines (the
 *                 CrptFl case: 27 charge columns → Description/Charge rows).
 */
export const ReshapeKind = z.enum(['flat', 'unpivot']);
/**
 * A OneView **destination format** — the import target a template maps INTO
 * (e.g. CrptFl, GncWanBlFl). Distinct from a template: many vendor templates
 * can target one destination. This registry is what the builder UI's
 * "destination" picker reads, what the AI compares an uploaded example
 * against, and where each format's authoritative OneView `<Transformation>`
 * definition + expected input columns live. Platform-level, kb_admin RBAC.
 */
export const OneViewDestinationFormat = z.object({
    id: DocId,
    /** OneView file reference / import code, e.g. "CrptFl", "GncWanBlFl". */
    code: z.string().min(1).max(80),
    displayName: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    /** The authoritative OneView `<Transformation>` XML for this import. */
    transformationXml: z.string().max(20000).default(''),
    /** Columns the import file must carry, in order, from the def's `inColName`. */
    inputColumns: z.array(ExtractorColumn).max(120).default([]),
    /** Delimited (CSV) vs fixedwidth, from the def's `InFile fileType`. */
    fileType: z.enum(['Delimited', 'fixedwidth', 'Database']).default('Delimited'),
    /** 1-indexed first data row (the def's `firstDataRow`); header is the row above. */
    firstDataRow: z.number().int().min(1).max(50).default(2),
    /** Default reshape a template targeting this format tends to need. */
    reshapeHint: ReshapeKind.default('flat'),
    /** Free-text notes surfaced in the builder UI (gotchas, NOT-NULL cols, etc.). */
    notes: z.string().max(4000).default(''),
    status: ExtractorTemplateStatus,
    version: z.number().int().min(0).default(1),
    createdBy: z.string().max(200).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const OneViewDestinationFormatCreateBody = OneViewDestinationFormat.omit({
    id: true,
    version: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
});
export const ExtractorTemplate = z.object({
    id: DocId,
    name: z.string().min(1).max(120),
    displayName: z.string().min(1).max(200),
    /**
     * Back-compat alias for `outputShape`. Kept identical to `outputShape`
     * so older readers (and the existing run docs) keep working. New code
     * should reference `outputShape`.
     */
    format: ExtractorFormat,
    /** Output shape this template targets — single value in v1. */
    outputShape: ExtractorOutputShape,
    /** Vendor this template applies to — drives Python rows-prompt dispatch. */
    vendor: ExtractorVendor,
    /** Canonical AccessProvider value the mapper writes (e.g. "BCX" for Telkom). */
    accessProvider: z.string().min(1).max(60),
    /** Optional override of the shared HEADER_PROMPT. Empty/undefined = use shared default. */
    headerPrompt: z.string().max(8000).optional(),
    /** Vision rows-pass prompt — the substantive content of the template. */
    rowsPrompt: z.string().min(1).max(16000),
    /**
     * Per-template output columns. When set, the Python mapper builds rows
     * dynamically from this list. When empty/absent AND `outputShape` is
     * `GncWanBlFl`, the legacy hardcoded 36-col mapper runs. Any other
     * outputShape with empty columns is a config error.
     */
    columns: z.array(ExtractorColumn).max(80).default([]),
    /**
     * Free-text rules accumulated by admins. Max 8000 chars — guard against
     * prompt-injection payload sprawl flagged in Phase 3 finding #12. Now
     * operator notes only; the active prompt content lives in `rowsPrompt`.
     */
    extractionRules: z.string().max(8000).default(''),
    /**
     * Declarative transform recipe (extractor v2). When set, the Python job runs
     * the recipe engine (unpivot/subtotals/preamble/EOF reshaping) instead of the
     * flat column-map reader — so a new destination format is a template edit,
     * not a code deploy. JSON-opaque here; the engine validates semantics.
     */
    recipe: z.record(z.unknown()).optional(),
    status: ExtractorTemplateStatus,
    /** Bumped on every rules edit. Runs snapshot this for reproducibility. */
    version: z.number().int().nonnegative(),
    createdBy: DocId,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
});
/**
 * Snapshot of a template captured on the run doc at run-creation time so
 * subsequent prompt edits don't change the prompt an in-flight run sees.
 * The Python extractor reads this off the run doc instead of importing the
 * vendor module — vendors module remains the safety-net fallback.
 */
export const ExtractorTemplateSnapshot = z.object({
    templateId: DocId,
    version: z.number().int().nonnegative(),
    vendor: ExtractorVendor,
    outputShape: ExtractorOutputShape,
    accessProvider: z.string().min(1).max(60),
    headerPrompt: z.string().max(8000).optional(),
    rowsPrompt: z.string().min(1).max(16000),
    /** Snapshot of the template's columns at run start. Empty = legacy path. */
    columns: z.array(ExtractorColumn).max(80).default([]),
    /** Snapshot of the template's recipe (v2) — drives the recipe engine. */
    recipe: z.record(z.unknown()).optional(),
});
export const ExtractorRun = z.object({
    id: DocId,
    tenantId: TenantId,
    /** Who kicked the run off. Team-shared visibility — `createdBy` style. */
    ownerUserId: DocId,
    sourceFileName: z.string().min(1).max(255),
    sourceMime: z.string().min(1).max(120),
    sourceBytes: z.number().int().nonnegative(),
    vendor: ExtractorVendor,
    templateId: DocId,
    /** Snapshot of the template version this run was launched against. */
    templateVersion: z.number().int().nonnegative().optional(),
    /**
     * Full prompt snapshot taken at run creation. The Python worker reads
     * `templateSnapshot.rowsPrompt` instead of importing from the vendors
     * module — that way mid-flight prompt edits in the admin UI never
     * mutate the prompt an in-flight run already saw. Optional for
     * back-compat: legacy runs (pre-template-snapshot) and runs whose
     * vendor has no active template fall back to the vendors module.
     */
    templateSnapshot: ExtractorTemplateSnapshot.optional(),
    status: ExtractorRunStatus,
    batches: z.array(ExtractorBatch).default([]),
    /**
     * Reconciliation between extracted line-item sum and invoice grand total.
     * `null` until Slice 2's validator runs; `'unknown'` when grand total
     * could not be identified.
     */
    grandTotalReconciliation: ExtractorReconciliation.nullable().default(null),
    /**
     * Numeric breakdown of the reconciliation result — populated by the
     * worker alongside `grandTotalReconciliation`. Optional for back-compat:
     * runs that finished before this field shipped show "data not yet
     * available" in the UI rather than crashing.
     */
    reconciliationSummary: ExtractorReconciliationSummary.optional(),
    costUsd: z.number().nonnegative().default(0),
    /** Refinement lineage. Walk the chain to enforce MAX_REFINEMENT_DEPTH. */
    parentRunId: DocId.optional(),
    /** Groups runs uploaded together in one bulk action so the whole batch can
     *  be selected + downloaded as a single combined CSV. */
    batchId: z.string().max(64).optional(),
    /** Audit fingerprint for the most recent download — required on mismatch. */
    downloadedBy: DocId.optional(),
    /** Free-text justification recorded when reconciliation=mismatch download proceeds. */
    downloadJustification: z.string().max(2000).optional(),
    errorMessage: z.string().max(2000).optional(),
    /** Soft-delete tombstone; child runs are auto-tombstoned with the parent. */
    deletedAt: IsoTimestamp.optional(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
});
// ---------- Request body shapes -------------------------------------------
export const ExtractorMintUploadUrlBody = z.object({
    sourceFileName: z.string().min(1).max(255),
    mimeType: z.enum([
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]),
    sizeBytes: z.number().int().positive(),
    templateId: DocId,
    /** Optional bulk-upload grouping id, shared across a multi-file upload. */
    batchId: z.string().max(64).optional(),
});
/** Combined-CSV export across several runs that share the same columns. */
export const ExtractorCombinedDownloadBody = z.object({
    runIds: z.array(DocId).min(1).max(50),
    /** Required when any selected run's reconciliation is a mismatch. */
    downloadJustification: z.string().max(2000).optional(),
});
export const ExtractorStartRunBody = z.object({
    /** Optional vendor override at kickoff — defaults to `unknown` until classifier runs. */
    vendor: ExtractorVendor.optional(),
});
export const ExtractorRefineBody = z.object({
    /**
     * Free-text refinement notes appended to the prompt for the child run.
     * Ephemeral — applies to this child run only, not the template.
     */
    notes: z.string().min(1).max(4000),
    /**
     * When true, the column-scope flags below are also promoted into the
     * parent run's template: appended as an addendum block to `rowsPrompt`,
     * version bumped, status set to `active`. Cell/row-scope flags are
     * never promoted — they're scoped to this one PDF.
     */
    updateTemplate: z.boolean().optional(),
    /**
     * Structured column-scope flags. Each entry becomes an addendum line in
     * the template when `updateTemplate` is true. Free-form `notes` above
     * still flows into the child run regardless.
     */
    columnFlags: z
        .array(z.object({
        column: z.string().min(1).max(120),
        note: z.string().min(1).max(500),
    }))
        .max(50)
        .optional(),
});
export const ExtractorDownloadFormat = z.enum(['csv', 'xlsx', 'json']);
export const ExtractorDownloadBody = z.object({
    /** Required when reconciliation=mismatch — short audit note per Phase 2 decision #20. */
    downloadJustification: z.string().min(1).max(2000).optional(),
});
/** Hard cap — matches plan §"Phase 3 finding #10" + audit-log entry. */
export const MAX_REFINEMENT_DEPTH = 5;
// ---------- Template CRUD body shapes (Slice 4) ---------------------------
/**
 * Create-template body. `status` is forced to `draft` server-side regardless
 * of what the client supplies — activation is a separate route so the audit
 * trail captures it as a deliberate operator action.
 */
export const ExtractorTemplateCreateBody = z.object({
    name: z.string().min(1).max(120),
    displayName: z.string().min(1).max(200),
    format: ExtractorFormat.optional(),
    outputShape: ExtractorOutputShape,
    vendor: ExtractorVendor,
    accessProvider: z.string().min(1).max(60),
    rowsPrompt: z.string().min(100).max(16000),
    headerPrompt: z.string().max(8000).optional(),
    extractionRules: z.string().max(8000).default(''),
    columns: z.array(ExtractorColumn).max(80).default([]),
    recipe: z.record(z.unknown()).optional(),
});
/**
 * Patch body. Every field optional so the admin can tweak rules without
 * resubmitting name/displayName. Status flips go through the dedicated
 * /activate route — Slice 4 keeps PATCH for content-only edits.
 *
 * Editing any prompt-y field (`rowsPrompt`, `headerPrompt`, structural
 * fields) bumps the version server-side — the patch handler is the
 * source of truth for that bump.
 */
export const ExtractorTemplatePatchBody = z
    .object({
    name: z.string().min(1).max(120).optional(),
    displayName: z.string().min(1).max(200).optional(),
    vendor: ExtractorVendor.optional(),
    accessProvider: z.string().min(1).max(60).optional(),
    outputShape: ExtractorOutputShape.optional(),
    rowsPrompt: z.string().min(100).max(16000).optional(),
    headerPrompt: z.string().max(8000).optional(),
    extractionRules: z.string().max(8000).optional(),
    columns: z.array(ExtractorColumn).max(80).optional(),
    recipe: z.record(z.unknown()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field is required.',
});
// ---------- Template builder (extractor v2) ----------------------------------
export const ExtractorBuilderUploadUrlBody = z.object({
    sizeBytes: z.number().int().positive(),
});
/** Fetch an import definition from the knowledgebase by OneView file reference. */
export const DestinationFormatFromKbBody = z.object({
    code: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/, 'code must be a slug'),
});
/** Parse a pasted OneView <Transformation> XML into a destination-format draft. */
export const DestinationFormatParseXmlBody = z.object({
    xml: z.string().min(20).max(20000),
});
export const ExtractorBuilderAnalyzeBody = z.object({
    /** GCS path returned by /builder/upload-url — must stay under builder/{tenant}/. */
    gcsPath: z.string().min(1).max(500),
    destinationFormatId: DocId,
    /** Present on refine iterations: the recipe the user reviewed. */
    priorRecipe: z.record(z.unknown()).optional(),
    /** Natural-language change request from the user, applied to priorRecipe. */
    feedback: z.string().max(4000).optional(),
});
export const ExtractorTemplateActivateBody = z.object({
    status: ExtractorTemplateStatus,
});
//# sourceMappingURL=extractor.js.map